import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import { useLocalization } from '../features/localization/LocalizationContext';
import { formatLocalMoney } from '../features/localization/locales';
import {
  BILLING_CYCLES,
  PRICING_PLANS,
  getDisplayedMonthly,
  getPlanPrice,
} from '../features/localization/pricing';

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const withLocalParam = (path, searchParams) => {
  const local = searchParams.get('local') || searchParams.get('locale') || searchParams.get('country');
  if (!local) return path;
  const next = new URLSearchParams();
  next.set('local', local);
  return `${path}?${next.toString()}`;
};

const Pricing = () => {
  const { local, profile } = useLocalization();
  const [searchParams] = useSearchParams();
  const [billing, setBilling] = useState('monthly');

  const currencyNote = useMemo(() => {
    const code = profile.currency?.code || 'USD';
    if (local === 'US') {
      return 'Prices in USD. Payment processing fees are billed separately.';
    }
    return `Prices in ${code} for ${profile.country}. Payment processing & messaging credits are billed separately.`;
  }, [local, profile]);

  return (
    <div className="min-h-screen bg-sand-100 font-body text-ink-900">
      <MarketingNav variant="light" current="/pricing" />

      <main>
        <section className="mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-6 sm:pt-24 lg:px-8">
          <motion.div {...reveal} className="mx-auto max-w-3xl text-center">
            <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">Pricing</p>
            <h1 className="mt-4 font-display text-4xl font-light leading-[1.08] tracking-tight text-ink-900 sm:text-6xl">
              Simple plans for medspas, salons &amp; spas
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-ink-700/80 sm:text-lg">
              Booking, payments and floor management — priced for how beauty businesses actually grow
              {profile.heroRegion ? ` in ${profile.heroRegion}` : ''}.
            </p>
          </motion.div>

          <motion.div
            {...reveal}
            className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
          >
            <div className="flex bg-sand-50 ring-1 ring-ink-900/10">
              {BILLING_CYCLES.map((cycle) => (
                <button
                  key={cycle.id}
                  type="button"
                  onClick={() => setBilling(cycle.id)}
                  className={`px-5 py-3 text-sm font-medium transition-colors ${
                    billing === cycle.id
                      ? 'bg-ink-900 text-sand-50'
                      : 'text-ink-800 hover:bg-white'
                  }`}
                >
                  {cycle.label}
                  {cycle.discountLabel && billing !== cycle.id ? (
                    <span className="ml-2 text-[11px] uppercase tracking-[0.12em] text-saffron-600">
                      Save
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
            <p className="text-sm text-ink-600/70">{currencyNote}</p>
          </motion.div>
        </section>

        <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-6 sm:pb-28 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-3 lg:gap-5">
            {PRICING_PLANS.map((plan, index) => {
              const displayMonthly = getDisplayedMonthly(plan, local, billing);
              const billedAmount = getPlanPrice(plan, local, billing);
              const periodLabel = billing === 'yearly' ? ' / mo, billed yearly' : ' / month';

              return (
                <motion.article
                  key={plan.id}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{
                    duration: 0.7,
                    delay: index * 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className={`flex flex-col p-6 sm:p-8 ${
                    plan.highlighted
                      ? 'bg-ink-900 text-sand-50'
                      : 'bg-sand-50 text-ink-900 ring-1 ring-ink-900/10'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h2 className="font-display text-3xl font-light">{plan.name}</h2>
                    {plan.highlighted && (
                      <span className="bg-saffron-500 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-900">
                        Popular
                      </span>
                    )}
                  </div>
                  <p
                    className={`mt-3 text-sm leading-relaxed ${
                      plan.highlighted ? 'text-sand-200/85' : 'text-ink-700/80'
                    }`}
                  >
                    {plan.tagline}
                  </p>

                  <div className="mt-8">
                    <p className="font-display text-4xl font-light tabular-nums sm:text-5xl">
                      {formatLocalMoney(displayMonthly, profile)}
                    </p>
                    <p
                      className={`mt-2 text-sm ${
                        plan.highlighted ? 'text-sand-300/80' : 'text-ink-600/70'
                      }`}
                    >
                      {periodLabel}
                      {billing === 'yearly' && (
                        <span className="block mt-1">
                          {formatLocalMoney(billedAmount, profile)} billed yearly
                        </span>
                      )}
                    </p>
                  </div>

                  <ul className="mt-8 flex-1 space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm sm:text-base">
                        <Check
                          className={`mt-0.5 h-5 w-5 flex-none ${
                            plan.highlighted ? 'text-saffron-400' : 'text-saffron-600'
                          }`}
                        />
                        <span className={plan.highlighted ? 'text-sand-100/90' : 'text-ink-800'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={withLocalParam('/signup', searchParams)}
                    className={`mt-10 inline-flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] transition-colors ${
                      plan.highlighted
                        ? 'bg-saffron-500 text-ink-900 hover:bg-saffron-400'
                        : 'bg-ink-900 text-sand-50 hover:bg-ink-800'
                    }`}
                  >
                    {plan.cta}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </motion.article>
              );
            })}
          </div>

          <motion.p
            {...reveal}
            className="mx-auto mt-12 max-w-2xl text-center text-sm leading-relaxed text-ink-600/70"
          >
            14-day free trial on Starter and Growth. No commission on your bookings — you keep every
            client you bring. *Messaging rates vary by country and carrier.
          </motion.p>
        </section>

        <section className="bg-ink-900 py-20 sm:py-28">
          <div className="mx-auto max-w-4xl px-5 text-center sm:px-6">
            <motion.h2
              {...reveal}
              className="font-display text-4xl font-light leading-[1.1] text-sand-50 sm:text-5xl"
            >
              Ready when your chairs are.
            </motion.h2>
            <motion.p
              {...reveal}
              className="mx-auto mt-5 max-w-xl text-base text-sand-200/85"
            >
              Start taking bookings this week. Switch plans as your team grows — no long contracts.
            </motion.p>
            <motion.div {...reveal} className="mt-10 flex justify-center">
              <Link
                to={withLocalParam('/signup', searchParams)}
                className="group inline-flex items-center gap-2 bg-saffron-500 px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
              >
                Sign up
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default Pricing;
